import * as React from 'react'
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  FloatingPortal,
  useDismiss,
  useInteractions,
  useHover,
  safePolygon,
} from '@floating-ui/react'
import { Editor } from '@tiptap/react'
import './source-popover.scss'
import { useQuery } from '@tanstack/react-query'
import { trpc } from '@/lib/trpc'
import { SourceTypeIcon } from '@/utils/file-icons'

interface SourcePopoverProps {
  editor: Editor
  onSourceClick?: (sourceId: string, sourceType: string) => void
}

export function SourcePopover({ editor, onSourceClick }: SourcePopoverProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [sourceElement, setSourceElement] = React.useState<HTMLElement | null>(null)
  const [sourceData, setSourceData] = React.useState<{
    sourceType: string
    sourceId: string
    sourceTitle: string
    snippet?: string
    number?: number
  } | null>(null)
  const [isHoveringCard, setIsHoveringCard] = React.useState(false)
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null)

  // Fetch source details
  const { data: sourceDetails } = useQuery({
    ...trpc.docSources.getSourceDetails.queryOptions({
      sourceType: sourceData?.sourceType || '',
      sourceId: sourceData?.sourceId || '',
    }),
    enabled: !!sourceData?.sourceType && !!sourceData?.sourceId,
  })

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom',
    middleware: [
      offset(8),
      flip({
        fallbackPlacements: ['top', 'bottom-start', 'bottom-end', 'top-start', 'top-end'],
      }),
      shift({ padding: 5 }),
    ],
    whileElementsMounted: autoUpdate,
  })

  const hover = useHover(context, {
    handleClose: safePolygon({
      blockPointerEvents: false,
      buffer: 1,
    }),
    delay: {
      open: 300,
      close: 0,
    },
    enabled: !!sourceElement,
  })

  const dismiss = useDismiss(context)
  const { getFloatingProps } = useInteractions([hover, dismiss])

  React.useEffect(() => {
    if (!editor) return

    const sourceRefs = new WeakMap<
      HTMLElement,
      { mouseenter: () => void; mouseleave: () => void }
    >()

    const setupSourceListeners = (sourceEl: HTMLElement) => {
      if (sourceRefs.has(sourceEl)) return

      const handleMouseEnter = () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }

        const sourceType = sourceEl.getAttribute('data-source-type') || ''
        const sourceId = sourceEl.getAttribute('data-source-id') || ''
        const sourceTitle = sourceEl.getAttribute('data-source-title') || ''
        const snippet = sourceEl.getAttribute('data-snippet') || ''
        const number = sourceEl.getAttribute('data-number')

        setSourceElement(sourceEl)
        setSourceData({
          sourceType,
          sourceId,
          sourceTitle,
          snippet,
          number: number ? parseInt(number, 10) : undefined,
        })
        refs.setReference(sourceEl)
        setIsOpen(true)
      }

      const handleMouseLeave = (event: MouseEvent) => {
        const relatedTarget = event.relatedTarget as HTMLElement | null
        const isMovingToCard = relatedTarget?.closest('.source-popover')

        if (!isMovingToCard) {
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
          }

          timeoutRef.current = setTimeout(() => {
            if (!isHoveringCard) {
              setSourceElement(null)
              setSourceData(null)
              setIsOpen(false)
            }
          }, 500)
        }
      }

      sourceEl.addEventListener('mouseenter', handleMouseEnter)
      sourceEl.addEventListener('mouseleave', handleMouseLeave)

      sourceRefs.set(sourceEl, { mouseenter: handleMouseEnter, mouseleave: handleMouseLeave })
    }

    const cleanupLinkListeners = (sourceEl: HTMLElement) => {
      const handlers = sourceRefs.get(sourceEl)
      if (handlers) {
        sourceEl.removeEventListener('mouseenter', handlers.mouseenter)
        sourceEl.removeEventListener('mouseleave', handlers.mouseleave)
        sourceRefs.delete(sourceEl)
      }
    }

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            const sources = node.querySelectorAll('[data-source-citation]')
            sources.forEach((sourceEl) => {
              setupSourceListeners(sourceEl as HTMLElement)
            })
          }
        })

        mutation.removedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            const sources = node.querySelectorAll('[data-source-citation]')
            sources.forEach((sourceEl) => {
              cleanupLinkListeners(sourceEl as HTMLElement)
            })
          }
        })
      })
    })

    const editorElement = editor.view.dom
    const initialSources = editorElement.querySelectorAll('[data-source-citation]')
    initialSources.forEach((sourceEl) => {
      setupSourceListeners(sourceEl as HTMLElement)
    })

    observer.observe(editorElement, {
      childList: true,
      subtree: true,
    })

    return () => {
      observer.disconnect()
      const allSources = editorElement.querySelectorAll('[data-source-citation]')
      allSources.forEach((sourceEl) => {
        cleanupLinkListeners(sourceEl as HTMLElement)
      })
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [editor, isHoveringCard, refs])

  const handleCardMouseEnter = () => {
    setIsHoveringCard(true)
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
  }

  const handleCardMouseLeave = () => {
    setIsHoveringCard(false)
    setIsOpen(false)
    setSourceElement(null)
    setSourceData(null)
  }

  if (!isOpen || !sourceData) return null

  return (
    <FloatingPortal>
      <div
        ref={refs.setFloating}
        style={floatingStyles}
        {...getFloatingProps()}
        className="source-popover min-w-[480px] max-w-[60vw] p-3 border shadow-[0_2px_16px_-1px_rgba(0,0,0,0.12),0_4px_8px_-2px_rgba(0,0,0,0.05)]"
        onMouseEnter={handleCardMouseEnter}
        onMouseLeave={handleCardMouseLeave}
      >
          <div className="flex items-center gap-2 mb-1.5">
        
            <SourceTypeIcon fileName={sourceData.sourceTitle || ''} type={sourceData.sourceType} />
            {/* <span className="source-number">[{sourceData.number || '?'}]</span> */}
            {/* <span className="source-type text-sm font-semibold leading-5">
              {sourceData.sourceType.replace('_', ' ')}
            </span> */}
            <span className='source-type text-sm font-semibold leading-5'>
              {sourceData.sourceTitle}  
            </span>
          </div>

        <div className="">
          {/* <h4 className="source-title">{sourceData.sourceTitle}</h4> */}

          {sourceData.snippet && (
            // <blockquote className="source-snippet">
            //   "{sourceData.snippet}"
            // </blockquote>
            <span className="text-xs text-muted-foreground line-clamp-5">
              {sourceData.snippet}
            </span>
          )}

          {/* {sourceDetails && (
            <div className="source-metadata">
              {sourceDetails.type === 'internal_doc' && sourceDetails.data && (
                <p className="text-muted-foreground text-xs">
                  Workspace: {sourceDetails.data.workspace?.name || 'Unknown'}
                </p>
              )}
            </div>
          )} */}
        </div>
      </div>
    </FloatingPortal>
  )
}
